/**
 * Integrations (API Key Management) dashboard.
 *
 * Lists every known provider in categorized sections. Each provider
 * card shows its current configured state and exposes Set / Test /
 * Delete / Enable-Disable actions.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  KeyRound,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  CircleSlash,
  RefreshCw,
  Trash2,
  Plus,
  ExternalLink,
} from 'lucide-react';
import toast from 'react-hot-toast';
import DashboardLayout from '../components/DashboardLayout';
import SetKeyModal from '../components/SetKeyModal';
import IntegrationBalancePanel from '../components/IntegrationBalancePanel';
import integrationService from '../services/integrationService';
import {
  IntegrationKey,
  IntegrationStatus,
  ProviderMetadata,
  IntegrationCategory,
} from '../types/integration';

const CATEGORY_ORDER: IntegrationCategory[] = [
  'ai_analysis',
  'investor_research',
  'syndication',
  'other',
];

const CATEGORY_LABELS: Record<IntegrationCategory, string> = {
  ai_analysis: 'AI & Analysis',
  investor_research: 'Investor Research',
  syndication: 'Syndication',
  other: 'Other',
};

const IntegrationsPage: React.FC = () => {
  const [providers, setProviders] = useState<ProviderMetadata[]>([]);
  const [keys, setKeys] = useState<IntegrationKey[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [testingId, setTestingId] = useState<number | null>(null);
  const [modalProvider, setModalProvider] = useState<ProviderMetadata | null>(null);

  const loadAll = async () => {
    setIsLoading(true);
    const [provRes, keysRes] = await Promise.all([
      integrationService.listProviders(),
      integrationService.listIntegrationKeys(),
    ]);
    if (provRes.error) toast.error(provRes.error);
    else if (provRes.data) setProviders(provRes.data);
    if (keysRes.error) toast.error(keysRes.error);
    else if (keysRes.data) setKeys(keysRes.data);
    setIsLoading(false);
  };

  useEffect(() => {
    loadAll();
  }, []);

  const keyByProvider = useMemo(() => {
    const map: Record<string, IntegrationKey | undefined> = {};
    keys.forEach((k) => {
      map[k.provider] = k;
    });
    return map;
  }, [keys]);

  const rotationSummary = useMemo(() => {
    const overdue = keys.filter((k) => k.rotation_status === 'overdue').length;
    const dueSoon = keys.filter((k) => k.rotation_status === 'due_soon').length;
    return { overdue, dueSoon };
  }, [keys]);

  const grouped = useMemo(() => {
    const groups: Record<IntegrationCategory, ProviderMetadata[]> = {
      ai_analysis: [],
      investor_research: [],
      syndication: [],
      other: [],
    };
    providers.forEach((p) => {
      const bucket = groups[p.category] || groups.other;
      bucket.push(p);
    });
    return groups;
  }, [providers]);

  const handleSave = async (provider: ProviderMetadata, key: string, label: string) => {
    const res = await integrationService.createIntegrationKey({
      provider: provider.provider,
      key,
      label: label || null,
    });
    if (res.error) {
      toast.error(res.error);
      throw new Error(res.error);
    }
    toast.success(`${provider.display_name} key saved`);
    await loadAll();
  };

  const handleTest = async (row: IntegrationKey, label: string) => {
    setTestingId(row.id);
    const res = await integrationService.testIntegrationKey(row.id);
    setTestingId(null);
    if (res.error) {
      toast.error(res.error);
      return;
    }
    if (res.data?.success) {
      toast.success(`${label}: ${res.data.detail}`);
    } else {
      toast.error(`${label}: ${res.data?.detail ?? 'Test failed'}`);
    }
    await loadAll();
  };

  const handleDelete = async (row: IntegrationKey, label: string) => {
    if (!window.confirm(`Delete the stored ${label} key?`)) return;
    const res = await integrationService.deleteIntegrationKey(row.id);
    if (res.error) {
      toast.error(res.error);
      return;
    }
    toast.success(`${label} key deleted`);
    await loadAll();
  };

  const handleToggle = async (row: IntegrationKey, label: string) => {
    const next: IntegrationStatus =
      row.status === 'active' ? 'disabled' : 'active';
    const res = await integrationService.updateIntegrationKey(row.id, {
      status: next,
    });
    if (res.error) {
      toast.error(res.error);
      return;
    }
    toast.success(`${label} ${next === 'active' ? 'enabled' : 'disabled'}`);
    await loadAll();
  };

  const renderStatusBadge = (row?: IntegrationKey) => {
    if (!row) {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md bg-gray-100 text-gray-600">
          <CircleSlash className="w-3 h-3 mr-1" />
          Not configured
        </span>
      );
    }
    if (row.status === 'invalid') {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md bg-red-100 text-red-700">
          <AlertTriangle className="w-3 h-3 mr-1" />
          Invalid
        </span>
      );
    }
    if (row.status === 'disabled') {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md bg-yellow-100 text-yellow-700">
          <CircleSlash className="w-3 h-3 mr-1" />
          Disabled
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md bg-green-100 text-green-700">
        <CheckCircle2 className="w-3 h-3 mr-1" />
        Active
      </span>
    );
  };

  const renderProviderCard = (provider: ProviderMetadata) => {
    const row = keyByProvider[provider.provider];
    const isConfigured = !!row;
    const isTesting = testingId === row?.id;

    return (
      <div
        key={provider.provider}
        className="bg-white border border-gray-200 rounded-xl p-5 flex flex-col"
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center space-x-2">
              <h3 className="text-base font-semibold text-gray-900">
                {provider.display_name}
              </h3>
              {provider.docs_url && (
                <a
                  href={provider.docs_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-gray-400 hover:text-blue-600"
                  aria-label="Provider docs"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
            </div>
            {row?.label && (
              <p className="text-xs text-gray-500 mt-0.5">{row.label}</p>
            )}
          </div>
          {renderStatusBadge(row)}
        </div>

        {provider.required_for.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {provider.required_for.map((feat) => (
              <span
                key={feat}
                className="inline-block text-[11px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100"
              >
                {feat}
              </span>
            ))}
          </div>
        )}

        <div className="mt-4 space-y-1 text-sm">
          {isConfigured ? (
            <>
              <div className="text-gray-600">
                <span className="text-gray-500">Key:&nbsp;</span>
                <span className="font-mono text-gray-900">
                  {row!.key_preview || '••••'}
                </span>
              </div>
              {row!.last_tested_at && (
                <div className="text-xs text-gray-500">
                  Last tested{' '}
                  {new Date(row!.last_tested_at).toLocaleString()}
                  {row!.last_test_result ? ` — ${row!.last_test_result}` : ''}
                </div>
              )}
              {row!.rotation_age_days !== null && (
                <div className="text-xs flex items-center gap-2 mt-1">
                  <span className="text-gray-500">
                    Last rotated {row!.rotation_age_days} day
                    {row!.rotation_age_days === 1 ? '' : 's'} ago
                  </span>
                  {row!.rotation_status === 'overdue' && (
                    <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-medium rounded bg-red-100 text-red-700">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      Rotate now ({Math.abs(row!.rotation_due_days ?? 0)}d
                      overdue)
                    </span>
                  )}
                  {row!.rotation_status === 'due_soon' && (
                    <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-medium rounded bg-amber-100 text-amber-800">
                      Rotate soon ({row!.rotation_due_days ?? 0}d left)
                    </span>
                  )}
                </div>
              )}
              {/* Balance + burn-rate + runway. Only show for AI
                  providers — other integrations (diffbot, resend, etc.)
                  aren't priced per-token so this readout is meaningless. */}
              {provider.category === 'ai_analysis' && (
                <IntegrationBalancePanel
                  integrationId={row!.id}
                  providerDisplayName={provider.display_name}
                />
              )}
            </>
          ) : (
            <div className="text-xs text-gray-500">
              Not configured — add key to enable{' '}
              {provider.required_for.slice(0, 2).join(', ') ||
                'this integration'}
              .
            </div>
          )}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={() => setModalProvider(provider)}
            className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700"
          >
            <Plus className="w-3.5 h-3.5 mr-1" />
            {isConfigured ? 'Rotate Key' : 'Set Key'}
          </button>
          {isConfigured && (
            <>
              <button
                onClick={() => handleTest(row!, provider.display_name)}
                disabled={isTesting}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {isTesting ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                ) : (
                  <RefreshCw className="w-3.5 h-3.5 mr-1" />
                )}
                Test
              </button>
              <button
                onClick={() => handleToggle(row!, provider.display_name)}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                {row!.status === 'active' ? 'Disable' : 'Enable'}
              </button>
              <button
                onClick={() => handleDelete(row!, provider.display_name)}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border border-red-200 text-red-700 hover:bg-red-50"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" />
                Delete
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <KeyRound className="w-6 h-6 mr-2 text-blue-600" />
              Integrations
            </h1>
            <p className="text-gray-600 mt-1">
              Manage encrypted API keys for external providers. Keys are
              stored encrypted at rest and never returned in full.
            </p>
          </div>
          <button
            onClick={loadAll}
            className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
        </div>

        {!isLoading &&
          (rotationSummary.overdue > 0 || rotationSummary.dueSoon > 0) && (
            <div
              className={`border rounded-lg p-4 flex items-start gap-3 ${
                rotationSummary.overdue > 0
                  ? 'border-red-200 bg-red-50 text-red-900'
                  : 'border-amber-200 bg-amber-50 text-amber-900'
              }`}
            >
              <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
              <div className="text-sm">
                <div className="font-semibold">
                  {rotationSummary.overdue > 0
                    ? `${rotationSummary.overdue} API key${
                        rotationSummary.overdue === 1 ? '' : 's'
                      } overdue for rotation`
                    : `${rotationSummary.dueSoon} API key${
                        rotationSummary.dueSoon === 1 ? '' : 's'
                      } due for rotation soon`}
                </div>
                <div className="text-xs mt-0.5 opacity-90">
                  Rotate keys regularly to limit blast radius if a credential
                  ever leaks. Click <em>Rotate Key</em> on the affected card
                  below — the new ciphertext replaces the old one and the
                  reminder resets.
                </div>
              </div>
            </div>
          )}

        {isLoading ? (
          <div className="bg-white border border-gray-200 rounded-xl p-10 flex items-center justify-center text-gray-600">
            <Loader2 className="w-5 h-5 mr-3 animate-spin" />
            Loading integrations...
          </div>
        ) : (
          CATEGORY_ORDER.map((cat) => {
            const group = grouped[cat] || [];
            if (group.length === 0) return null;
            return (
              <section key={cat}>
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  {CATEGORY_LABELS[cat]}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {group.map(renderProviderCard)}
                </div>
              </section>
            );
          })
        )}
      </div>

      <SetKeyModal
        isOpen={!!modalProvider}
        onClose={() => setModalProvider(null)}
        provider={modalProvider}
        existingLabel={
          modalProvider ? keyByProvider[modalProvider.provider]?.label : null
        }
        onSave={async (key, label) => {
          if (modalProvider) {
            await handleSave(modalProvider, key, label);
          }
        }}
      />
    </DashboardLayout>
  );
};

export default IntegrationsPage;
